/**
 * AggregateEntityList - Display affected entities in an aggregate event
 *
 * Shows a collapsible list of entities affected by an aggregate event,
 * sorted by severity with "Show more" pagination.
 */

import React, { useState, useMemo } from 'react';
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  MapPin,
  Truck,
  User,
  Warehouse,
  FileText,
  Calendar,
  Shield,
  ClipboardCheck,
} from 'lucide-react';
import type { AffectedEntity, AffectedEntityType } from '../../types';
import './AggregateEntityList.css';

export interface AggregateEntityListProps {
  /** List of affected entities (should be pre-sorted by severity) */
  entities: AffectedEntity[];

  /** Initial number of entities to show */
  initialCount?: number;

  /** Number of additional entities to show per "Show more" click */
  expandIncrement?: number;

  /** Domain link for viewing all entities */
  domainLink?: {
    label: string;
    href: string;
  };

  /** Callback when an entity is clicked */
  onEntityClick?: (entityId: string) => void;

  /** Callback when the domain link is clicked */
  onDomainLinkClick?: () => void;

  /** Additional CSS class */
  className?: string;
}

const ENTITY_ICONS: Record<AffectedEntityType, React.FC<{ className?: string }>> = {
  field: MapPin,
  equipment: Truck,
  person: User,
  animal_group: () => <span className="aggregate-entity-list__icon-text">🐷</span>,
  storage: Warehouse,
  document: FileText,
  deadline: Calendar,
  compliance: Shield,
  inspection: ClipboardCheck,
};

const SEVERITY_ORDER: Record<AffectedEntity['severity'], number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

export const AggregateEntityList: React.FC<AggregateEntityListProps> = ({
  entities,
  initialCount = 5,
  expandIncrement = 5,
  domainLink,
  onEntityClick,
  onDomainLinkClick,
  className = '',
}) => {
  const [visibleCount, setVisibleCount] = useState(initialCount);

  // Sort entities by severity
  const sortedEntities = useMemo(() => {
    return [...entities].sort(
      (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
    );
  }, [entities]);

  const visibleEntities = sortedEntities.slice(0, visibleCount);
  const hasMore = visibleCount < sortedEntities.length;
  const remainingCount = sortedEntities.length - visibleCount;

  const handleShowMore = () => {
    setVisibleCount((prev) => Math.min(prev + expandIncrement, sortedEntities.length));
  };

  const formatDate = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (entities.length === 0) {
    return null;
  }

  return (
    <div className={`aggregate-entity-list ${className}`}>
      <ul className="aggregate-entity-list__items">
        {visibleEntities.map((entity) => {
          const Icon = ENTITY_ICONS[entity.type];
          return (
            <li
              key={entity.id}
              className={`aggregate-entity-list__item aggregate-entity-list__item--${entity.severity}`}
            >
              <button
                type="button"
                className="aggregate-entity-list__item-button"
                onClick={() => onEntityClick?.(entity.id)}
              >
                <Icon className="aggregate-entity-list__item-icon" />
                <div className="aggregate-entity-list__item-content">
                  <span className="aggregate-entity-list__item-name">{entity.name}</span>
                  <span className="aggregate-entity-list__item-status">{entity.status}</span>
                  {entity.dueDate && (
                    <span className="aggregate-entity-list__item-due">
                      Frist: {formatDate(entity.dueDate)}
                    </span>
                  )}
                </div>
                <ChevronRight className="aggregate-entity-list__item-chevron" />
              </button>
            </li>
          );
        })}
      </ul>

      {hasMore && (
        <button
          type="button"
          className="aggregate-entity-list__show-more"
          onClick={handleShowMore}
        >
          <ChevronDown className="aggregate-entity-list__show-more-icon" />
          {remainingCount} weitere anzeigen
        </button>
      )}

      {domainLink && (
        <a
          href={domainLink.href}
          className="aggregate-entity-list__domain-link"
          onClick={(e) => {
            if (onDomainLinkClick) {
              e.preventDefault();
              onDomainLinkClick();
            }
          }}
        >
          <ExternalLink className="aggregate-entity-list__domain-link-icon" />
          {domainLink.label}
        </a>
      )}
    </div>
  );
};

export default AggregateEntityList;
